import { useEffect, useRef } from 'react';

import { useGeoMarkupStore, type GeoMarkupStore } from '@/store';
import { ensureAppBootstrap, resetAppBootstrap } from '@/store/appBootstrap';
import { extractSettings, SYNCED_SETTING_KEYS } from '@/store/settingsBridge';
import { settingsService } from '@/services/api/settingsService';

const SETTINGS_SAVE_DEBOUNCE_MS = 1500;

/** Cheap reference-equality check for synced state slices */
function getSyncStateRefs(state: GeoMarkupStore) {
  return {
    taskFilters: state.taskFilters,
    noteFilters: state.noteFilters,
    uiTaskViewMode: state.ui.taskViewMode,
    uiNoteViewMode: state.ui.noteViewMode,
    uiToolbarGuide: state.ui.toolbarGuide.hasSeenGuide,
    uiScrollHint: state.ui.toolbarScrollHint.hasSeenScrollHint,
    uiSwipeDown: state.ui.slidePanel.swipeDownHintShown,
    uiSwipeUp: state.ui.slidePanel.swipeUpHintShown,
    uiDashboard: state.ui.dashboard.dateRange,
    searchPlaceTypes: state.search.selectedPlaceTypes,
    searchLocationPriority: state.search.useLocationPriority,
    guideDismissed: state.guides.dismissedGuides,
    mapIs3D: state.map.is3DMode,
    mapShareLocation: state.map.shareLocation,
    mapUserBasemaps: state.map.userBasemapByWorkspace,
  };
}

type SyncStateRefs = ReturnType<typeof getSyncStateRefs>;

function syncStateChanged(prev: SyncStateRefs, curr: SyncStateRefs): boolean {
  return Object.keys(prev).some(
    (key) =>
      prev[key as keyof SyncStateRefs] !== curr[key as keyof SyncStateRefs]
  );
}

/**
 * Auto-saves user preferences from the Zustand store to the UserSettings API.
 *
 * Loading is handled once by the app bootstrap (`ensureAppBootstrap`), which
 * applies the server settings before the app renders. This hook only:
 * - waits for the bootstrap to finish, then snapshots the applied settings
 * - subscribes to store changes and persists diffs with a debounce
 *
 * Must be called once in ProtectedRoute.
 */
export function useSettingsSync(): void {
  const isAuthenticated = useGeoMarkupStore(
    (state) => state.auth.isAuthenticated
  );
  const userId = useGeoMarkupStore((state) => state.auth.user?.id ?? null);
  const loadedForUserRef = useRef<string | null>(null);
  const lastSnapshotRef = useRef<Record<string, string> | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      // Reset on logout so the next login re-bootstraps from the server.
      loadedForUserRef.current = null;
      lastSnapshotRef.current = null;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      resetAppBootstrap();
      return;
    }

    // Skip if auto-save is already wired for this user
    if (loadedForUserRef.current === userId) return;
    loadedForUserRef.current = userId;

    let cancelled = false;

    const startAutoSave = async () => {
      // Settings are loaded + applied by the bootstrap; just wait for it.
      await ensureAppBootstrap();
      if (cancelled) return;

      // Snapshot the applied settings for change detection.
      lastSnapshotRef.current = extractSettings(useGeoMarkupStore.getState());

      // Subscribe to store changes for auto-save (targeted).
      let prevSyncState = getSyncStateRefs(useGeoMarkupStore.getState());
      unsubscribeRef.current = useGeoMarkupStore.subscribe(() => {
        const currentSyncState = getSyncStateRefs(useGeoMarkupStore.getState());
        if (!syncStateChanged(prevSyncState, currentSyncState)) return;
        prevSyncState = currentSyncState;

        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => {
          const currentSnapshot = extractSettings(useGeoMarkupStore.getState());
          const changedSettings = getChangedSettings(
            lastSnapshotRef.current,
            currentSnapshot
          );

          if (Object.keys(changedSettings).length === 0) return;

          lastSnapshotRef.current = currentSnapshot;
          settingsService
            .batchUpdate({ settings: changedSettings })
            .catch((error: unknown) =>
              console.error('Failed to save settings:', error)
            );
        }, SETTINGS_SAVE_DEBOUNCE_MS);
      });
    };

    startAutoSave();

    return () => {
      cancelled = true;
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [isAuthenticated, userId]);
}

/** Compare snapshots and return only changed categories */
function getChangedSettings(
  previous: Record<string, string> | null,
  current: Record<string, string>
): Record<string, string> {
  if (!previous) return current;

  const changed: Record<string, string> = {};
  for (const key of SYNCED_SETTING_KEYS) {
    const currentValue = current[key];
    if (currentValue !== undefined && previous[key] !== currentValue) {
      changed[key] = currentValue;
    }
  }
  return changed;
}
